<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import InputText from 'primevue/inputtext';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import axios from "axios";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import FileUpload from 'primevue/fileupload';
import Button from 'primevue/button';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: "question-edit",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      v$: useVuelidate(),
      questionId: this.$route.params.id,
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      title: "",
      description: "",
      fileData: null,
      is_file: true,
      formSending: false,
      lesson: null,
      lessons: [],
      unit: null,
      units: [],
      branch: null,
      branches: [],
      degree: null,
      type: 'mcq',
      typeOptions: [
        { label: 'اختيار من متعدد', value: 'mcq' },
        { label: 'إجابة قصيرة', value: 'short_answer' }
      ],
      difficulty_level: null,
      difficultyOptions: [
        { label: 'سهل', value: 'easy' },
        { label: 'متوسط', value: 'medium' },
        { label: 'صعب', value: 'hard' }
      ],
      answers: [],
    };
  },
  components: {
    Breadcrumb,
    InputText,
    Dropdown,
    InputSwitch,
    FileUpload,
    Button
  },
  validations() {
    return {
      title: { required },
      degree: { required },
    };
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope ? `/courses/${this.courseId}/question-bank` : `/${this.gradeSlug}/questions`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: "بنك الأسئلة", path: this.basePath }, { name: "تعديل سؤال", path: `${this.basePath}/edit/${this.questionId}` }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: "سؤال", path: this.basePath },
        { name: "تعديل سؤال", path: `/${this.gradeSlug}/question/edit/${this.questionId}` },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() { return this.resolvedGrade?.id ?? null; },
    isMCQ() {
      return this.type === 'mcq';
    }
  },
  methods: {
    fetchQuestion() {
      axios.get(`/questions/${this.questionId}`)
          .then(res => {
            const data = res.data.data;
            this.title = data.title;
            this.description = data.description;
            this.type = data.type;
            this.difficulty_level = data.difficulty_level;
            this.is_file = data.is_file;
            this.degree = data.degree;
            this.fileData = null;
            this.answers = data.answers.map(a => ({
              title: a.title,
              image: null,
              correct: !!a.correct,
              question_id: a.question_id
            }));


            // fetch lesson hierarchy
            this.fetchBranch().then(() => {
              this.branch = this.branches.find(b => b.id === data.lesson?.parent?.parent?.id) || null;
              if (this.branch) {
                this.fetchUnits().then(() => {
                  this.unit = this.units.find(u => u.id === data.lesson?.parent?.id) || null;
                  if (this.unit) {
                    this.fetchLessons().then(() => {
                      this.lesson = this.lessons.find(l => l.id === data.lesson?.id) || null;
                    });
                  }
                });
              }
            });
          });
    },
    onFileChange(event) {
      if (event && event.target && event.target.files) {
        this.fileData = event.target.files[0];
      }
    },
    onAnswerImageChange(event, index) {
      if (event && event.target && event.target.files) {
        this.answers[index].image = event.target.files[0];
      }
    },
    addAnswer() {
      if (this.answers.length < 4) {
        this.answers.push({ title: '', image: null, correct: false, question_id: null });
      } else {
        Swal.fire({ icon: 'warning', title: 'Maximum answers reached', text: 'You can only add up to 4 answers.' });
      }
    },
    removeAnswer(idx) {
      if (this.answers.length > 2) {
        this.answers.splice(idx, 1);
      } else {
        Swal.fire({ icon: 'warning', title: 'لا يمكن الحذف', text: 'يجب أن يكون هناك إجابتين على الأقل.' });
      }
    },
    setCorrect(idx) {
      this.answers = this.answers.map((a, i) => ({
        ...a,
        correct: i === idx
      }));
    },
    sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;

      const formData = new FormData();
      formData.append('title', this.title);
      formData.append('description', this.description);
      formData.append('lesson_id', this.lesson?.id ?? this.unit?.id ?? this.branch?.id);
      formData.append('type', this.type);
      formData.append('difficulty_level', this.difficulty_level);
      formData.append('is_file', this.is_file ? 1 : 0);

      if (this.is_file && this.fileData) {
        formData.append('file', this.fileData);
      }

      if (this.isMCQ) {
        this.answers.forEach((answer, idx) => {
          formData.append(`answers[${idx}][title]`, answer.title);
          formData.append(`answers[${idx}][correct]`, answer.correct ? 1 : 0);
          if (answer.image) {
            formData.append(`answers[${idx}][image]`, answer.image);
          }
        });
      }

      this.formSending = true;
      axios.post(`/questions/${this.questionId}?_method=PUT`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
          .then(() => {
            Swal.fire({ icon: "success", title: "تم التعديل بنجاح" });
            this.$router.push(this.basePath);
          })
          .catch((error) => {
            Swal.fire({ icon: "error", title: error.response?.data?.message || "حدث خطأ" });
          })
          .finally(() => {
            this.formSending = false;
          });
    },
    async fetchBranch() {
      const params = this.isCourseScope
          ? { type: 1, course_id: this.courseId }
          : { type: 1, academic_year_id: this.gradeId };
      try {
        const response = await axios.get('/lessons', { params });
        this.branches = response.data.data;
      } catch {
        this.branches = [];
      }
    },
    async fetchUnits() {
      const branchId = this.branch?.id;
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId, parent_id: branchId }
          : { type: 2, academic_year_id: this.gradeId, parent_id: branchId };
      try {
        const response = await axios.get('/lessons', { params });
        this.units = response.data.data;
      } catch {
        this.units = [];
      }
    },
    async fetchLessons() {
      const unitId = this.unit?.id;
      const params = this.isCourseScope
          ? { type: 3, course_id: this.courseId, parent_id: unitId }
          : { type: 3, academic_year_id: this.gradeId, parent_id: unitId };
      try {
        const response = await axios.get('/lessons', { params });
        this.lessons = response.data.data;
      } catch {
        this.lessons = [];
      }
    },
    customLabelBranch(option) { return option ? `${option.title} (${option.description})` : ''; },
    customLabelUnit(option) { return option ? `${option.title} (${option.description})` : ''; },
    customLabelLesson(option) { return option ? `${option.title} (${option.description})` : ''; },
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    this.fetchQuestion();
  }
}
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <form class="row" @submit.prevent="sendForm()">
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الفروع</label>
          <Dropdown v-model="branch" :options="branches" showClear :optionLabel="customLabelBranch"
                    placeholder="اختر الفرع" @update:model-value="fetchUnits" />
        </div>
      </div>

      <!-- Unit -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الوحدات</label>
          <Dropdown v-model="unit" :options="units" showClear :optionLabel="customLabelUnit"
                    placeholder="اختر الوحده" @update:model-value="fetchLessons" />
        </div>
      </div>

      <!-- Lesson -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الدروس</label>
          <Dropdown v-model="lesson" :options="lessons" showClear :optionLabel="customLabelLesson"
                    placeholder="اختر الدرس" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="title">اسم السؤال</label>
          <InputText id="title" v-model="title" aria-describedby="title-help" />
          <span class="text-danger" v-if="v$.title.$error">برجاء ادخال اسم المحتوى</span>
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>النوع</label>
          <Dropdown v-model="type" :options="typeOptions" disabled optionLabel="label" optionValue="value" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>مستوى الصعوبة</label>
          <Dropdown v-model="difficulty_level" :options="difficultyOptions" optionLabel="label" optionValue="value" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="degree">درجة السؤال</label>
          <InputNumber id="degree" v-model="degree" inputId="degree" :useGrouping="false" fluid />
          <span class="text-danger" v-if="v$.degree.$error">برجاء ادخال درجة السؤال</span>
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <label>الملف</label>
        <input type="file" @change="onFileChange" class="form-control" />
      </div>


      <!-- Answers Section (Only for MCQ) -->
      <div v-if="isMCQ" class="col-12 mt-4">
        <h5 class="mb-3">الإجابات</h5>
        <div v-for="(answer, idx) in answers" :key="idx" class="border rounded p-3 mb-3 position-relative answer-card">
          <div class="row g-3 align-items-center">
            <div class="col-md-6">
              <label class="form-label">نص الإجابة {{ idx + 1 }}</label>
              <InputText v-model="answer.title" placeholder="أدخل نص الإجابة" class="w-100" />
            </div>
            <div class="col-md-3">
              <label class="form-label">صورة (اختياري)</label>
              <FileUpload mode="basic" accept="image/*" @select="(e) => onAnswerImageChange(e, idx)" chooseLabel="صورة" />
            </div>
            <div class="col-md-2 text-center">
              <label class="form-label d-block">صحيحة؟</label>
              <InputSwitch v-model="answer.correct" @update:model-value="setCorrect(idx)" />
            </div>
            <div class="col-md-1 text-end">
              <button class="btn btn-danger" type="button" @click="removeAnswer(idx)"
                      :disabled="answers.length <= 2"><i class="pi pi-trash"></i></button>
            </div>
          </div>
          <small v-if="answer.correct" class="text-success mt-1 d-block">
            <i class="pi pi-check-circle"></i> هذه الإجابة الصحيحة
          </small>
        </div>
        <button label="إضافة إجابة جديدة" type="button" class="btn btn-success" @click="addAnswer" :disabled="answers.length >= 4">
          <i class="pi pi-plus"></i>
        </button>
      </div>
      <div class="col-lg-4 col-md-6 col-12 my-2"></div>
      <div class="col-lg-4 col-md-6 col-12 my-2"></div>
      <div class="col-lg-4 col-md-6 col-12 my-2">
        <button type="submit" class="btn primaryButton w-100" :disabled="formSending">
          <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
          {{ formSending ? 'جاري التعديل...' : 'تعديل' }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
/* Copy styles from add.vue */
</style>